import React from "react";
import { Button, Column, Conditional, Else, If, makeClassName, Row } from "@hex-insights/core";
import { GuardianDetailQuery } from "../../../Utilities";
import { PersonForm } from "../Person";
import { UserForm, UserOnPersonForm } from "../User";
import * as GuardianForm from "./Form";

export type ControlledDetailProps = Omit<
	GuardianForm.ControlledDetailProps,
	"guardian" | "formStateArgs" | "onSuccess"
> & {
	guardian: GuardianDetailQuery["guardian"];
	guardianFormStateArgs: GuardianForm.ControlledDetailProps["formStateArgs"];
	guardianOnSuccess: GuardianForm.ControlledDetailProps["onSuccess"];
	personFormStateArgs: PersonForm.ControlledDetailProps["formStateArgs"];
	personOnSuccess: PersonForm.ControlledDetailProps["onSuccess"];
	userFormStateArgs: UserForm.ControlledDetailProps["formStateArgs"];
	userOnSuccess: UserForm.ControlledDetailProps["onSuccess"];
};

export function ControlledDetail({
	guardian,
	guardianFormStateArgs,
	personFormStateArgs,
	userFormStateArgs,
	guardianOnSuccess,
	personOnSuccess,
	userOnSuccess,
	...props
}: ControlledDetailProps) {
	const [view, setView] = React.useState<"person" | "guardian" | "user">("person");

	return (
		<Column justify="spaced-start" verticalSpacing="1.25rem">
			<Row justify="spaced-start">
				<Button
					onClick={() => setView("person")}
					className={makeClassName("button--nav", view === "person" ? "button--nav--active" : "")}
				>
					Personal Details
				</Button>
				<Button
					onClick={() => setView("guardian")}
					className={makeClassName("button--nav", view === "guardian" ? "button--nav--active" : "")}
				>
					Guardian Details
				</Button>
				<Button
					onClick={() => setView("user")}
					className={makeClassName("button--nav", view === "user" ? "button--nav--active" : "")}
				>
					Account Details
				</Button>
			</Row>

			<Conditional>
				<If condition={view === "person"}>
					<PersonForm.ControlledDetail
						person={guardian.person}
						formStateArgs={personFormStateArgs}
						onSuccess={personOnSuccess}
						{...props}
					/>
				</If>
				<If condition={view === "guardian"}>
					<GuardianForm.ControlledDetail
						guardian={guardian}
						formStateArgs={guardianFormStateArgs}
						onSuccess={guardianOnSuccess}
						{...props}
					/>
				</If>
				<If condition={view === "user"}>
					<Conditional>
						<If condition={!!guardian.person.user}>
							{!!guardian.person.user && (
								<UserForm.ControlledDetail
									user={guardian.person.user}
									formStateArgs={userFormStateArgs}
									onSuccess={userOnSuccess}
									{...props}
								/>
							)}
						</If>
						<Else>
							<Row justify="center">
								<UserOnPersonForm.CreateController personID={guardian.person.id} />
							</Row>
						</Else>
					</Conditional>
				</If>
			</Conditional>
		</Column>
	);
}
